/* eslint-disable camelcase */
import React, { useContext, useEffect, useMemo, useRef } from 'react';
import _ from 'lodash';

import analytics from '@nerdwallet/analytics';
import {
  useSegmentAnalytics,
  getDeployableInfo,
  useAnalyticsPageView,
} from '@nerdwallet/seo-analytics';
import useIsInViewport, { CallbackRef } from 'use-is-in-viewport';
import { SegmentEventPayloadT } from './trackers';
import { EventType } from './event-types';
import { SegmentEventTrackerT } from './types';

type SegmentImpressionContextType = {
  eventId: string;
  pageViewId: string;
};

interface FormViewedAttributes {
  entity_name: string;
  form_name: string;
}

interface FeatureViewedAttributes {
  entity_name: string;
  section_name: string;
  item_position: number | null;
  item_copy: string;
  content_id: number | null;
  additional_attributes?: [Record<string, boolean | string | number>];
  parent_event_id: string;
}

interface LinkViewedAttributes {
  entity_name: string;
  link_copy: string;
  section_name: string | null;
  link_position: number | null;
  destination_url: string;
  destination_content_id: number | null;
}

export type SegmentAttributes<T extends EventType> = T extends 'form_viewed'
  ? FormViewedAttributes
  : T extends 'feature_viewed'
  ? FeatureViewedAttributes
  : T extends 'link_viewed'
  ? LinkViewedAttributes
  : never;

interface SegmentImpressionProps<T extends EventType> {
  children: React.ReactNode;
  component?: React.ElementType;
  enabled?: boolean;
  eventType: T;
  attributes: SegmentAttributes<T>;
}

const trackViewedImpression: SegmentEventTrackerT<
  SegmentEventPayloadT,
  void
> = (eventType, payload, segmentInstance) => {
  segmentInstance.track(eventType, {
    ...payload,
  });
};

const SegmentImpressionContext =
  React.createContext<SegmentImpressionContextType | null>(null);

const useSegmentImpressionContext = (): SegmentImpressionContextType => {
  const context = useContext<SegmentImpressionContextType>(
    SegmentImpressionContext as React.Context<SegmentImpressionContextType>
  );
  return context;
};

const hashEvent = (attributes) => JSON.stringify(attributes);

const useSegmentImpressionHook = ({
  enabled = true,
  eventType,
  attributes,
}: {
  enabled?: boolean;
  eventType: EventType;
  attributes: SegmentAttributes<EventType>;
}): {
  eventId: string;
  pageViewId: string;
  ref: CallbackRef;
} => {
  const segment = useSegmentAnalytics()?.analytics;
  const { pageViewId } = useAnalyticsPageView();

  // Track if the element is visible
  const [isVisible, ref] = useIsInViewport();

  // Track the previous event to avoid duplicate events
  const previousEvent = useRef(null);
  const newEvent = useMemo(
    () => ({
      eventType,
      attributes,
    }),
    [eventType, attributes]
  );

  // Track the event id to avoid duplicate events
  const evtHash = hashEvent(attributes);
  const eventId = useMemo(() => analytics.uuid(), []);

  const debouncedTrackViewedImpression = useMemo(() => {
    return _.debounce(
      (event, segmentAttributes, segmentInstance) =>
        trackViewedImpression(event, segmentAttributes, segmentInstance),
      1000
    );
  }, []);

  useEffect(() => {
    if (
      enabled &&
      segment &&
      isVisible &&
      !_.isEqual(newEvent, previousEvent.current)
    ) {
      const { deployable_name, deployable_version } = getDeployableInfo();
      debouncedTrackViewedImpression(
        eventType,
        {
          ...attributes,
          deployable_name,
          deployable_version,
          event_id: eventId,
          pageview_id: pageViewId,
        },
        segment
      );

      previousEvent.current = newEvent;
    }
  }, [
    attributes,
    debouncedTrackViewedImpression,
    enabled,
    evtHash,
    eventId,
    eventType,
    isVisible,
    newEvent,
    pageViewId,
    segment,
  ]);

  return { eventId, pageViewId, ref };
};

const SegmentImpression: React.FC<SegmentImpressionProps<EventType>> = ({
  children,
  component: Component = 'div',
  enabled = true,
  eventType,
  attributes,
}) => {
  const { eventId, pageViewId, ref } = useSegmentImpressionHook({
    enabled,
    eventType,
    attributes,
  });

  return (
    <SegmentImpressionContext.Provider value={{ eventId, pageViewId }}>
      {enabled ? <Component ref={ref}>{children}</Component> : children}
    </SegmentImpressionContext.Provider>
  );
};

export {
  useSegmentImpressionContext,
  useSegmentImpressionHook,
  SegmentImpressionContext,
};
export default SegmentImpression;
